// BootstrapWizard — 3-step first-run setup (token+name → passkey → backup codes).
//
// Backend contract is unchanged: a single POST to /api/auth/bootstrap with
// { token, username, display_name } returns { totp_secret, backup_codes }.
// Steps 2 and 3 are pure UI reveal — the WebAuthn enrollment hand-off lives in
// /settings/account, and the backup codes display the response.

"use client";

import { useRouter } from "@/i18n/navigation";
import { ApiClientError } from "@/lib/api";
import { PALETTE_LABELS } from "@/lib/palette";
import { usePaletteStore } from "@/lib/palette-store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const schema = z.object({
  token: z.string().min(8, "Bootstrap token from systemd journal"),
  username: z
    .string()
    .min(3, "≥ 3 chars")
    .regex(/^[a-z0-9_-]+$/, "lowercase, digits, `_-`"),
  display_name: z.string().optional(),
  password: z
    .string()
    .optional()
    .refine((v) => !v || v.length >= 8, "≥ 8 chars"),
});

type Form = z.infer<typeof schema>;

interface BootResponse {
  totp_secret?: string;
  backup_codes?: string[];
  user_id?: string;
  tenant_id?: string;
  csrf_token?: string;
}

/** Step metadata. ``key`` resolves to ``auth.bootstrap.steps.<key>`` in
 *  the catalog; ``jp`` is the decorative kanji glyph (palette aesthetic,
 *  untouched by locale). */
const STEP_DEFS: Array<{ key: "name" | "passkey" | "backup"; jp: string }> = [
  { key: "name", jp: "名" },
  { key: "passkey", jp: "鍵" },
  { key: "backup", jp: "控" },
];

export function BootstrapWizard() {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [printed, setPrinted] = useState<BootResponse | null>(null);
  const [confirmed, setConfirmed] = useState("");
  const palette = usePaletteStore((s) => s.palette);
  const tBoot = useTranslations("auth.bootstrap");

  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { token: "", username: "", display_name: "", password: "" },
  });

  async function submit(values: Form) {
    try {
      const res = await fetch("/api/auth/bootstrap", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new ApiClientError(res.status, `HTTP_${res.status}`, text || res.statusText);
      }
      const body = (await res.json()) as BootResponse;
      setPrinted(body);
      setStep(3);
      toast.success(tBoot("toasts.created"));
    } catch (e) {
      const msg = e instanceof Error ? e.message : tBoot("toasts.failed");
      toast.error(msg);
    }
  }

  return (
    <div style={{ width: "100%", maxWidth: 480 }} data-testid="bootstrap-wizard">
      <div className="auth-brand">
        <div className="mark">
          <span suppressHydrationWarning>{PALETTE_LABELS[palette].jp}</span>
        </div>
        <div className="name">
          <span className="jp">起</span>vc-engine · bootstrap
        </div>
        <div className="tagline">{tBoot("tagline")}</div>
      </div>

      <div className="boot-card">
        <div className="boot-progress">
          {STEP_DEFS.map((s, i) => {
            const n = i + 1;
            const active = step === n;
            const done = step > n;
            return (
              <div
                key={s.key}
                className={`boot-step ${active ? "active " : ""}${done ? "done" : ""}`}
              >
                <div className="num-row">
                  <div className="num">{done ? "✓" : n}</div>
                  <div>
                    <div className="lbl">{tBoot("stepShort", { n })}</div>
                  </div>
                  <span className="jp">{s.jp}</span>
                </div>
                <div className="name">{tBoot(`steps.${s.key}`)}</div>
              </div>
            );
          })}
        </div>

        <div className="boot-body">
          {step === 1 && (
            <>
              <div className="head">
                <div className="lbl">
                  <span className="jp">名</span>NAME YOUR INSTALL
                </div>
                <h2>{tBoot("step1.title")}</h2>
                <p className="desc">
                  A short label that distinguishes this install in logs and on backups.
                  Pick something that's still recognisable two years from now.
                </p>
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const ok = await form.trigger([
                    "display_name",
                    "username",
                    "password",
                  ]);
                  if (ok) setStep(2);
                }}
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                <label
                  htmlFor="display_name"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 9.5,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: "var(--subtle-fg)",
                  }}
                >
                  DISPLAY NAME
                </label>
                <input
                  id="display_name"
                  className="install-name"
                  placeholder="vc-engine · J. Tanaka · tokyo"
                  {...form.register("display_name")}
                />
                <label
                  htmlFor="username"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 9.5,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: "var(--subtle-fg)",
                  }}
                >
                  USERNAME (lowercase)
                </label>
                <input
                  id="username"
                  className="install-name"
                  autoComplete="username"
                  {...form.register("username")}
                />
                {form.formState.errors.username && (
                  <p style={{ color: "var(--danger)", fontSize: 11 }}>
                    {form.formState.errors.username.message}
                  </p>
                )}
                <label
                  htmlFor="password"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 9.5,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: "var(--subtle-fg)",
                  }}
                >
                  PASSWORD (optional · ≥ 8 chars)
                </label>
                <input
                  id="password"
                  className="install-name"
                  type="password"
                  autoComplete="new-password"
                  placeholder="leave blank to use passkey + backup codes only"
                  {...form.register("password")}
                />
                {form.formState.errors.password && (
                  <p style={{ color: "var(--danger)", fontSize: 11 }}>
                    {form.formState.errors.password.message}
                  </p>
                )}
                <button
                  type="submit"
                  className="passkey-btn"
                  style={{ width: "100%" }}
                  data-testid="bootstrap-step1-next"
                >
                  <span className="label">
                    <span className="top">{tBoot("step1.continue")}</span>
                    <span className="sub">NEXT · TOKEN ENTRY</span>
                  </span>
                </button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <div className="head">
                <div className="lbl">
                  <span className="jp">鍵</span>BOOTSTRAP TOKEN
                </div>
                <h2>{tBoot("step2.title")}</h2>
                <p className="desc">
                  Printed to the systemd journal on first boot — <code>journalctl -u vc-api</code>{" "}
                  shows it once. After submit we create your owner account and reveal backup codes.
                </p>
              </div>
              <form
                onSubmit={form.handleSubmit(submit)}
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                <label
                  htmlFor="token"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 9.5,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: "var(--subtle-fg)",
                  }}
                >
                  BOOTSTRAP TOKEN
                </label>
                <input
                  id="token"
                  className="install-name"
                  autoComplete="off"
                  style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}
                  {...form.register("token")}
                />
                {form.formState.errors.token && (
                  <p style={{ color: "var(--danger)", fontSize: 11 }}>
                    {form.formState.errors.token.message}
                  </p>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="button"
                    className="totp-toggle"
                    style={{ flex: 1 }}
                    onClick={() => setStep(1)}
                  >
                    <span className="grow-label">← Back</span>
                  </button>
                  <button
                    type="submit"
                    className="passkey-btn"
                    style={{ flex: 2 }}
                    disabled={form.formState.isSubmitting}
                    data-testid="bootstrap-create"
                  >
                    <span className="label">
                      <span className="top">
                        {form.formState.isSubmitting ? tBoot("step2.creating") : tBoot("step2.create")}
                      </span>
                      <span className="sub">REVEAL BACKUP CODES</span>
                    </span>
                  </button>
                </div>
              </form>
            </>
          )}

          {step === 3 && printed && (
            <>
              <div className="head">
                <div className="lbl">
                  <span className="jp">控</span>SAVE BACKUP CODES
                </div>
                <h2>{tBoot("step3.title")}</h2>
                <p className="desc">
                  Use these only if you lose access to your passkey. Each code works exactly
                  once. <strong>We won't show them again.</strong>
                </p>
              </div>

              <div className="boot-codes-grid">
                {(printed.backup_codes ?? []).map((c, i) => (
                  <div key={c} className="boot-code">
                    <span>{c}</span>
                    <span className="n">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                ))}
                {(!printed.backup_codes || printed.backup_codes.length === 0) && (
                  <div className="boot-code" style={{ gridColumn: "span 2" }}>
                    <span style={{ color: "var(--muted-fg)" }}>
                      Backup codes not generated yet — enroll in Settings → Account.
                    </span>
                  </div>
                )}
              </div>

              {printed.totp_secret ? (
                <div
                  style={{
                    marginTop: 10,
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    color: "var(--muted-fg)",
                  }}
                >
                  TOTP secret · <code>{printed.totp_secret}</code>
                </div>
              ) : null}

              <div className="boot-codes-bar">
                <button
                  type="button"
                  className="totp-toggle"
                  onClick={() => {
                    void navigator.clipboard.writeText(
                      (printed.backup_codes ?? []).join("\n"),
                    );
                    toast.success(tBoot("step3.copied"));
                  }}
                  data-testid="bootstrap-copy-codes"
                >
                  <span className="grow-label">{tBoot("step3.copyAll")}</span>
                </button>
                <button
                  type="button"
                  className="totp-toggle"
                  onClick={() => {
                    const blob = new Blob([(printed.backup_codes ?? []).join("\n")], {
                      type: "text/plain",
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "vc-engine-backup-codes.txt";
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <span className="grow-label">{tBoot("step3.download")}</span>
                </button>
              </div>

              <div className="boot-confirm">
                <span>I've stored these somewhere safe — type</span>
                <input
                  value={confirmed}
                  onChange={(e) => setConfirmed(e.target.value)}
                  placeholder="STORED"
                  data-testid="bootstrap-confirm"
                />
                <span>to finish.</span>
              </div>

              <div className="boot-codes-bar" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="passkey-btn"
                  style={{ width: "100%" }}
                  disabled={confirmed !== "STORED"}
                  onClick={() => router.push("/login")}
                  data-testid="bootstrap-finish"
                >
                  <span className="label">
                    <span className="top">{tBoot("step3.finish")}</span>
                    <span className="sub">CONTINUE → /login</span>
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default BootstrapWizard;
